import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, FileCheck, BadgeCheck, Check, Star, Quote } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SERVICES } from "@/data/services";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG = "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=85&w=1600&auto=format&fit=crop";
const FOUNDER_IMG = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=85&w=800&auto=format&fit=crop";

const STATS = [
  { value: "1,420+", label: "UK-standard CVs delivered" },
  { value: "89%", label: "Interview callback rate" },
  { value: "4.9/5", label: "Client satisfaction" },
  { value: "100%", label: "Aligned to GMT hours" },
];

const TESTIMONIALS = [
  { name: "Priya S.", role: "MSc Data Science graduate — Software Engineer, London", quote: "As an international student on a Graduate visa, my CV got zero replies in three months. Two weeks after RMS rewrote it, I had four interview calls and one offer from a fintech in Canary Wharf." },
  { name: "Arjun M.", role: "International nursing graduate — NHS, Manchester", quote: "The STAR interview coaching was a revelation. I walked into my NHS panel knowing exactly how they would score me — and it showed." },
  { name: "Fatima K.", role: "MSc Finance graduate — Analyst, Edinburgh", quote: "They understand what international students face in British hiring. The LinkedIn overhaul alone brought three recruiter messages in the first week." },
  { name: "Rohan D.", role: "MBA graduate — Project Manager, Birmingham", quote: "The bundle paid for itself. CV, cover letter, job-search strategy and sponsorship positioning — everything a student needs to land that first UK role." },
];

const FAQS = [
  { q: "I'm an international student — is this for me?", a: "Yes — international students are our core clients. Whether you're on a Student visa approaching graduation, a Graduate visa, or switching to a Skilled Worker visa, we build your CV, LinkedIn and job-search strategy around UK sponsorship rules and the two-year post-study window." },
  { q: "How fast is the turnaround?", a: "Every service offers two speeds: Basic delivery in 10 working days, or Advanced priority in 3–5 working days. Every order is handled personally by our UK HR team — never outsourced to template writers." },
  { q: "Do you guarantee my CV will pass ATS screening?", a: "Every CV we deliver is parsed through the same logic used by UK enterprise ATS platforms (Workday, Taleo, Greenhouse). Our documents currently pass at a 98.4% rate. We use single-column layouts, standard headings and mirrored job-description keywords." },
  { q: "I need Skilled Worker sponsorship. Can you still help?", a: "Absolutely — sponsorship positioning is one of our specialisations. We state your visa status professionally, target licensed sponsors from the Home Office register, and frame your skills so sponsorship becomes a business case rather than an objection. We also advise on making the most of your Graduate visa window." },
  { q: "How do payments work from outside the UK?", a: "All prices are fixed in GBP (£) and processed securely by Stripe — debit card, credit card and Apple Pay are all accepted, from anywhere in the world. You receive an instant receipt and order confirmation." },
  { q: "What if I don't like the first draft?", a: "The CV service includes two full revision rounds. You review the draft, send consolidated feedback through your client portal, and we refine until it sounds like you at your best." },
];

export default function Home() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const featured = [SERVICES[0], SERVICES[4], SERVICES[5]];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <img src={HERO_IMG} alt="London skyline" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0F1D]/80" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 40%, rgba(10,15,29,0.1) 0%, rgba(10,15,29,0.95) 75%)" }} />
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-5 pb-28 pt-24 sm:pt-32 lg:px-8 lg:pb-40 lg:pt-40">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <span className="overline-label inline-flex items-center gap-3" data-testid="hero-overline">
              <span className="h-px w-10 bg-[#C5A059]" />
              UK HR Experts · Sheffield, United Kingdom
            </span>
          </motion.div>

          <h1 className="mt-8 font-serif text-5xl font-semibold leading-[1.02] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <MaskedLine delay={0.15}>Get Hired in the UK.</MaskedLine>
            <MaskedLine delay={0.35} className="italic text-[#C5A059]">Crafted by UK</MaskedLine>
            <MaskedLine delay={0.55}>HR Experts.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subheadline"
          >
            Elite CVs, cover letters, LinkedIn overhauls and interview coaching for international students entering the UK job market — built to British corporate standards. From £41.80, fixed in GBP.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link to="/services" className="btn-gold" data-testid="hero-cta-packages">
              Explore Packages <ArrowRight size={16} />
            </Link>
            <Link to="/order" className="btn-ghost" data-testid="hero-cta-order">
              Start Your Order
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3"
            data-testid="trust-banner"
          >
            {["100% British Standards", "ATS Verified", "UK HR Professionals"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-slate-400">
                <ShieldCheck size={15} className="text-[#C5A059]" /> {t}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* PROBLEM / SOLUTION */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-36" data-testid="problem-solution-section">
        <Reveal>
          <span className="overline-label">The Problem</span>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold leading-tight text-slate-50 sm:text-4xl lg:text-5xl">
            Generic CVs die inside the ATS. <span className="italic text-[#C5A059]">British rules are different.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          <Reveal delay={0.1}>
            <div className="card-surface h-full p-8 lg:p-10" data-testid="generic-cv-card">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-red-400">The Generic Resume — Rejected</span>
              <ul className="mt-7 space-y-4">
                {["Home-country CV format — breaks the UK two-page golden rule", "Photo, date of birth and full address — instant red flags in Britain", "Two-column graphics layout that ATS parsers scramble into noise", "Foreign degree titles and grading scales UK recruiters can't parse", "Visa status hidden or vague — employers reject the ambiguity"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-slate-400">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rotate-45 bg-red-400/70" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="card-surface gold-stroke h-full p-8 lg:p-10" data-testid="rms-cv-card">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#C5A059]">The RMS Crafted CV — Shortlisted</span>
              <ul className="mt-7 space-y-4">
                {["Strict two-page British layout, single column, ATS-clean", "Professional summary written through a UK recruiter's lens", "Your international degree translated into UK-equivalent terms", "Keywords mirrored from real UK graduate scheme job descriptions", "Graduate / Skilled Worker visa status positioned as a business case"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-slate-200">
                    <Check size={16} className="mt-0.5 shrink-0 text-[#C5A059]" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        {/* STATS */}
        <div className="mt-20 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-700/50 lg:grid-cols-4" data-testid="stats-grid">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="bg-[#0F172A]">
              <div className="p-8 lg:p-10">
                <div className="font-serif text-4xl font-semibold text-[#C5A059] lg:text-5xl" data-testid={`stat-value-${i}`}>{s.value}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.15em] text-slate-400">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="border-y border-slate-800/60 bg-[#0C1222]" data-testid="featured-services-section">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <span className="overline-label">Services</span>
              <h2 className="mt-4 font-serif text-3xl font-semibold text-slate-50 sm:text-4xl lg:text-5xl">
                Priced in pounds. <span className="italic text-[#C5A059]">Built for Britain.</span>
              </h2>
            </div>
            <Link to="/services" className="btn-ghost" data-testid="featured-view-all-btn">
              View All Services <ArrowRight size={16} />
            </Link>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {featured.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.1}>
                <div
                  className={`card-surface group flex h-full flex-col p-8 transition-colors duration-300 hover:border-[#C5A059]/50 ${s.popular ? "gold-stroke" : ""}`}
                  data-testid={`home-service-card-${s.id}`}
                >
                  {s.popular && <span className="mb-4 w-fit rounded-full bg-[#C5A059] px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0A0F1D]">Best Value</span>}
                  <div className="font-mono text-3xl text-slate-50">£{s.price.toFixed(2)}</div>
                  <h3 className="mt-3 font-serif text-2xl font-semibold text-slate-50">{s.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{s.description}</p>
                  <button onClick={() => navigate(`/order?package=${s.id}`)} className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#C5A059] transition-colors group-hover:text-[#D4AF37]" data-testid={`home-service-cta-${s.id}`}>
                    Order this service <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER TEASER */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-36" data-testid="founder-teaser-section">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl border border-[#C5A059]/30" />
              <img src={FOUNDER_IMG} alt="RMS Group founder" className="relative aspect-[4/5] w-full rounded-2xl object-cover object-top" data-testid="founder-teaser-image" />
              <div className="absolute -bottom-6 -right-4 rounded-xl border border-slate-700/60 bg-[#111827] px-6 py-4 shadow-2xl sm:-right-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">London agency price</div>
                <div className="mt-1 font-mono text-sm text-slate-500 line-through">£250+</div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">RMS offshore price</div>
                <div className="mt-1 font-mono text-2xl font-semibold text-[#C5A059]">£77</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <span className="overline-label">The Founder</span>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-slate-50 sm:text-4xl">
              From screening CVs in a London boardroom to running a UK career consultancy from Sheffield.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-300">
              RMS Group was founded by a UK HR professional after years inside British corporate hiring — reading thousands of applications from brilliant international students and graduates who were rejected for formatting mistakes, not ability.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              Headquartered in Sheffield with a branch in Hyderabad, India, RMS Group delivers genuine UK recruiting expertise at mid-market prices — a fraction of what a London agency charges. Same insight. Same standards. One third of the price.
            </p>
            <Link to="/about" className="btn-ghost mt-8" data-testid="founder-teaser-cta">
              Read the full story <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y border-slate-800/60 bg-[#0C1222]" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
          <Reveal>
            <span className="overline-label">Student Success Stories</span>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold text-slate-50 sm:text-4xl lg:text-5xl">
              International students hired across <span className="italic text-[#C5A059]">London, Manchester & Edinburgh.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <div className="card-surface h-full p-8" data-testid={`testimonial-card-${i}`}>
                  <Quote size={22} className="text-[#C5A059]" />
                  <p className="mt-5 font-serif text-xl italic leading-relaxed text-slate-200">"{t.quote}"</p>
                  <div className="mt-6 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-50">{t.name}</div>
                      <div className="text-xs text-slate-500">{t.role}</div>
                    </div>
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} size={13} className="fill-[#C5A059] text-[#C5A059]" />
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 py-24 lg:px-8 lg:py-32" data-testid="faq-section">
        <Reveal className="text-center">
          <span className="overline-label">Questions</span>
          <h2 className="mt-4 font-serif text-3xl font-semibold text-slate-50 sm:text-4xl">Everything UK job seekers ask us</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-slate-800" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-lg font-medium text-slate-100 hover:text-[#C5A059] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden border-t border-slate-800/60" data-testid="final-cta-section">
        <div className="absolute inset-0 bg-[#064E3B]/20" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 text-center lg:px-8 lg:py-32">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-serif text-4xl font-semibold leading-tight text-slate-50 sm:text-5xl">
              Your first UK job deserves a <span className="italic text-[#C5A059]">recruiter's eye.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base text-slate-300">
              Select your service, check out securely in GBP, and tell us about your degree and visa status — your first UK-standard draft lands within days.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link to="/order" className="btn-gold" data-testid="final-cta-order-btn">
                Start Your Order <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-ghost" data-testid="final-cta-contact-btn">
                Ask a Question
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.15em] text-slate-500">
              <span className="inline-flex items-center gap-2"><FileCheck size={14} className="text-[#C5A059]" /> 2-page UK standard</span>
              <span className="inline-flex items-center gap-2"><BadgeCheck size={14} className="text-[#C5A059]" /> ATS verified</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck size={14} className="text-[#C5A059]" /> Secure Stripe checkout</span>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
